import { z } from 'zod';
import { hospitalCodeSchema } from './hospital';
import {
  SK_CLAIM_MODE_CODES,
  SK_CLAIM_TYPE_CODES,
  SK_CORPORATION_INDICATOR_CODES,
  SK_FORM_TYPE_CODES,
  SK_SPECIAL_CIRCUMSTANCES_CODES,
} from './provinceClaimFields';

const optionalClaimCode = (codes: readonly string[], label: string) =>
  z
    .string()
    .refine((value) => !value.trim() || codes.includes(value.toUpperCase()), {
      message: `${label} must be a supported Saskatchewan code or blank`,
    })
    .optional()
    .nullable();

export const claimStatusEnumValues = [
  'Paid',
  'Written-off',
  'Partially-paid',
  'Submitted',
  'In-progress',
  'Cancelled',
] as const;

export type ClaimStatusValue = (typeof claimStatusEnumValues)[number];

export const CLAIM_STATUS = {
  PAID: 'Paid',
  WRITTEN_OFF: 'Written-off',
  PARTIALLY_PAID: 'Partially-paid',
  SUBMITTED: 'Submitted',
  IN_PROGRESS: 'In-progress',
  CANCELLED: 'Cancelled',
} as const satisfies Record<string, ClaimStatusValue>;

export const nonTerminalClaimStatusEnumValues = [
  CLAIM_STATUS.PARTIALLY_PAID,
  CLAIM_STATUS.SUBMITTED,
  CLAIM_STATUS.IN_PROGRESS,
] as const satisfies readonly ClaimStatusValue[];

export const baseClaimSchema = z.object({
  id: z.guid(),
  version: z.number().default(1),
  patientID: z.uuid().nullable(),
  providerID: z.uuid(),
  siteID: z.uuid(),
  healthcardID: z.uuid().nullable(),
  payorID: z.uuid(),
  messageID: z.string().optional().nullable(),
  statementID: z.uuid().optional().nullable(),
  batchID: z.uuid().optional().nullable(),
  groupID: z.guid().optional().nullable(),
  createdBy: z.uuid().optional().nullable(),
  status: z.enum(claimStatusEnumValues).nullable(), // TODO(claim-status-migration-cleanup): make this non-nullable once all claims have been backfilled with a status
  serviceStart: z.coerce.date(),
  serviceEnd: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable(),
  claimNumber: z
    .string()
    .regex(
      /^(\d{5}|\d{9})$/,
      'Claim number must be a 5-digit SK number or 9-digit MB number'
    )
    .optional()
    .nullable(),
  providerComments: z.string().optional().nullable(),
  clerkComments: z.string().optional().nullable(),
  providerNotes: z.string().optional().nullable(),
  overhead: z.number().or(z.string()).nullable(),
  chiroService: z
    .string()
    .length(3, 'Chiro Service Code must be 3 characters')
    .optional()
    .nullable(),
  icd9: z.string().optional().nullable(),
  interpretingRadiologist: z
    .string()
    .length(5, 'Interpreting Radiologist must be 5 characters')
    .optional()
    .nullable(),
  serviceLocationIndicator: z
    .string()
    .length(1, 'Service Location must be 1 character')
    .regex(
      /^(C|O|E|R|S|X| )$/,
      'must be a supported provincial service location code or blank'
    )
    .optional()
    .nullable(),
  locationOfService: z
    .string()
    .length(1, 'location of service must be 1 character')
    .regex(
      /^(?:[A-Z]|[1-5]|9)$/,
      'location of service must be a supported provincial location code'
    )
    .nullable()
    .optional(),
  virtualPhysicianRegion: z
    .string()
    .regex(
      /^(B|N|O|R|W|C|V|Winnipeg|Brandon|Other|Rural|Northern|Remote|Virtual)$/,
      'virtual physician region must be one of B (Brandon), N (Northern), O (Other), R (Rural), W (Winnipeg), C (Remote), or V (Virtual)'
    )
    .nullable()
    .optional(),
  optometristReason: z
    .string()
    .length(2, 'Optometrist Reason must be 2 characters')
    .regex(
      /^(15|16|17|18|19|20|21|22|23|24|25|26|27|28|00)$/,
      'must be a two digit number between 15-28 or 00'
    )
    .nullable()
    .optional(),
  patientCount: z
    .number()
    .min(1, 'Patient Count must be at least 1')
    .max(99, 'Patient Count must be less than 100')
    .or(z.string().regex(/^\d+$/))
    .nullable()
    .optional(),
  thirdPartyLiability: z
    .string()
    .length(1, 'Third Party Liability must be 1 character')
    .regex(/^(T|W| )$/, 'third party liable must be one of T, W, or blank')
    .nullable()
    .optional(),
  incorporated: z
    .string()
    .regex(/^(0|1)$/, 'Incorporated must be 0 or 1 if supplied')
    .nullable()
    .optional(),
  claimType: optionalClaimCode(SK_CLAIM_TYPE_CODES, 'Claim Type'),
  claimMode: optionalClaimCode(SK_CLAIM_MODE_CODES, 'Claim Mode'),
  formType: optionalClaimCode(SK_FORM_TYPE_CODES, 'Form Type'),
  corporationIndicator: optionalClaimCode(
    SK_CORPORATION_INDICATOR_CODES,
    'Corporation Indicator'
  ),
  specialCircumstances: optionalClaimCode(
    SK_SPECIAL_CIRCUMSTANCES_CODES,
    'Special Circumstances'
  ),
  confidentialCode: z
    .string()
    .regex(/^(C| )$/, 'Must be C if supplied')
    .nullable()
    .optional(),
  referringProvider: z.string().optional().nullable(),
  hospitalName: z.string().optional().nullable(),
  hospitalCode: hospitalCodeSchema.optional().nullable(),
  facilityNumber: z
    .string()
    .max(5, 'Facility Number must be at most 5 characters')
    .optional()
    .nullable(),
  intakeItemID: z.uuid().optional().nullable(),
});

export const claimSummarySchema = baseClaimSchema
  .pick({
    id: true,
    claimNumber: true,
  })
  .strict();
